import { TFunction } from 'i18next';

import { ClientConfiguration, Permission } from '@Models';
import { SidebarIcons } from '../icons';
import { MenuItem } from '../types';
import { buildDocumentationItems, buildTrainingVideosItem } from './documentation';

/**
 * Default (bops) flavor menu.
 *
 * Sections: Configuration (Credentials + Masters + Policy configs) →
 * Recommendation (permission-gated) → MFA (config-gated) → Admin → Toolkit.
 */
const buildDefaultMenu = (configuration: ClientConfiguration | undefined, t: TFunction): MenuItem[] => {
  const insightsItem: MenuItem = {
    key: 'insights',
    label: t('Sidebar.Insights'),
    externalLink: 'https://www.bops.ai/resources-hub',
    icon: SidebarIcons.insights,
    enabled: true,
  };

  const credentialsItem: MenuItem | null =
    configuration?.connectorTypes && !configuration?.hasMultipleAccounts
      ? {
          key: 'credentials',
          label: t('Sidebar.Credentials'),
          link: '/credentials',
          icon: SidebarIcons.credentials,
          enabled: true,
        }
      : null;

  const policies = configuration?.policyConfiguration ?? [];
  // Group all policies under a single collapsible "Policies" parent so a long
  // policyConfiguration list doesn't dominate the Configuration section. Falls
  // back to nothing when there are no policies.
  const policiesItem: MenuItem | null =
    policies.length > 0
      ? {
          key: 'policies',
          label: t('Sidebar.Policies'),
          icon: SidebarIcons.policies,
          enabled: true,
          submenu: policies.map((policy) => ({
            key: `policy-${policy.id}`,
            label: policy.title,
            icon: policy.type === 'cpfr' ? SidebarIcons.policyCpfr : SidebarIcons.policyOther,
            link: `/configuration/${policy.type}/${policy.id}`,
            enabled: true,
          })),
        }
      : null;

  const items: (MenuItem | null)[] = [
    {
      key: 'configuration',
      label: t('Sidebar.Configuration'),
      icon: SidebarIcons.configuration,
      submenu: [
        ...(credentialsItem ? [credentialsItem] : []),
        {
          key: 'masters',
          label: t('Sidebar.Master'),
          icon: SidebarIcons.file,
          link: '/configuration/masters',
          enabled: true,
        },
        ...(policiesItem ? [policiesItem] : []),
      ],
    },
    {
      key: 'recommendation',
      permission: Permission.NavRecommendation,
      label: t('Sidebar.Main'),
      icon: SidebarIcons.dashboard,
      link: '/recommendation',
      hideWhenDisabled: true,
    },
    {
      key: 'mfa',
      label: 'MFA',
      icon: SidebarIcons.mfa,
      link: '/mfa',
      hideWhenDisabled: true,
      hide: !configuration?.phones,
    },
    {
      key: 'admin',
      label: 'Admin',
      icon: SidebarIcons.admin,
      submenu: [
        {
          key: 'users',
          permission: Permission.ManageUsers,
          label: t('Sidebar.Users'),
          icon: SidebarIcons.users,
          link: '/users',
          enabled: true,
          hideWhenDisabled: true,
        },
        {
          key: 'manualUpload',
          permission: Permission.ManualUpload,
          label: t('Sidebar.ManualUpload'),
          icon: SidebarIcons.manualUpload,
          link: '/manualUpload',
          enabled: true,
          hideWhenDisabled: true,
        },
        {
          key: 'integrations',
          permission: Permission.ManageIntegrations,
          label: t('Sidebar.Integrations'),
          icon: SidebarIcons.integrations,
          link: '/integrations',
          enabled: true,
          hideWhenDisabled: true,
        },
      ],
    },
    {
      key: 'toolkit',
      label: t('Sidebar.Toolkit'),
      icon: SidebarIcons.toolkit,
      submenu: [
        insightsItem,
        ...buildDocumentationItems(configuration?.dataRepository, t),
        ...buildTrainingVideosItem(configuration?.resources, t),
      ],
    },
  ];

  return items.filter((item): item is MenuItem => item !== null);
};

export default buildDefaultMenu;
